import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const LandingTemplate = ({ eyebrow, h1, sub, cta, ctaTo, bullets, steps, faqs, extraSchema, children, testid }) => {
  useEffect(() => {
    const schemas = [];
    if (faqs?.length) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    if (extraSchema) schemas.push(extraSchema);
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "landing-jsonld";
    el.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
    document.head.appendChild(el);
    return () => el.remove();
  }, [faqs, extraSchema]);

  return (
    <div className="mx-auto max-w-4xl px-6 py-14 md:px-10" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.25em] text-accent">{eyebrow}</p>
      <h1 className="mt-3 max-w-2xl font-display text-4xl font-medium tracking-tight sm:text-5xl">{h1}</h1>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{sub}</p>
      <Button asChild size="lg" className="mt-7 h-12 px-7" data-testid={`${testid}-cta`}>
        <Link to={ctaTo}>{cta} <ArrowRight className="ml-2 h-4 w-4" /></Link>
      </Button>

      {bullets?.length > 0 && (
        <ul className="mt-12 grid gap-4 sm:grid-cols-2">
          {bullets.map((b) => (
            <li key={b.title} className="rounded-2xl border border-border bg-card p-5">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              <p className="mt-3 font-medium">{b.title}</p>
              <p className="mt-1 text-sm text-muted-foreground">{b.text}</p>
            </li>
          ))}
        </ul>
      )}

      {steps?.length > 0 && (
        <ol className="mt-12 space-y-3">
          {steps.map((s, i) => (
            <li key={s} className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{i + 1}</span>
              <span className="text-sm">{s}</span>
            </li>
          ))}
        </ol>
      )}

      {children}

      {faqs?.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-lg md:text-xl">Frequently asked questions</h2>
          <div className="mt-4 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card px-5 py-4">
                <summary className="cursor-pointer list-none text-sm font-medium">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <div className="mt-14 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/10 via-card to-card p-8 text-center">
        <h2 className="font-display text-lg md:text-xl">Ready to start?</h2>
        <p className="mt-2 text-sm text-muted-foreground">Free to join — no card needed.</p>
        <Button asChild className="mt-4 h-11 px-6">
          <Link to={ctaTo}>{cta}</Link>
        </Button>
      </div>
    </div>
  );
};
